// The zone handle's pure rules for the create form (readable intake addresses §3.1.1, §3.1.3): no React and
// no antd here, so they can run under node:test as cloud.ts does.

/** The control plane's handle rule (§3.1.1): 3–24 characters, single hyphens, never leading or trailing. */
export const HANDLE = /^(?=.{3,24}$)[a-z0-9]+(?:-[a-z0-9]+)*$/u;
export const HANDLE_MAX = 24;

/* eslint-disable i18next/no-literal-string -- slug filter words matched against a name, never shown */
const DROPPED = new Set([
  'v', 'vs', 'versus', 're',
  'ltd', 'limited', 'inc', 'llc', 'llp', 'plc', 'corp', 'corporation', 'co', 'gmbh', 'sa', 'pte', 'pty', 'holdings', 'group',
  'the', 'of', 'and', 'for', 'a', 'an', 'by', 'to',
]);
const PHRASES = [['in', 'the', 'matter', 'of'], ['in', 're']] as const;
/* eslint-enable i18next/no-literal-string */

/**
 * The handle prefilled from the zone name, by the project-part slug rules (§3.4.2) without transliteration:
 * accents are dropped and any other non-Latin word is dropped, so a non-Latin name prefills nothing. Null
 * when the result is not a valid handle.
 */
export function suggestHandle(name: string): string | null {
  // eslint-disable-next-line i18next/no-literal-string -- `&` read as the slug word `and`
  const words = name.normalize('NFKC').replace(/&/gu, ' and ').replace(/[^\p{L}\p{N}\s.-]/gu, ' ')
    .split(/[\s.-]+/u)
    .map(word => word.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]/gu, ''))
    .filter(word => word !== '');
  const kept: string[] = [];
  for (let index = 0; index < words.length;) {
    const phrase = PHRASES.find(parts => parts.every((part, at) => words[index + at] === part));
    if (phrase !== undefined) { index += phrase.length; continue; }
    if (!DROPPED.has(words[index]!)) kept.push(words[index]!);
    index += 1;
  }
  const chosen = kept.length === 0 ? words : kept;
  let slug = '';
  for (const word of chosen) {
    const next = slug === '' ? word.slice(0, HANDLE_MAX) : `${slug}-${word}`;
    if (next.length > HANDLE_MAX) break;
    slug = next;
  }
  return HANDLE.test(slug) ? slug : null;
}

/** The helper line's example: the typed handle once valid, else `acme` (card 22). */
export function exampleHandle(value: unknown): string {
  // eslint-disable-next-line i18next/no-literal-string -- an example handle, a machine name in any language
  return typeof value === 'string' && HANDLE.test(value) ? value : 'acme';
}

/** What the availability check knows of one handle (§3.1.3); `unsupported` is a Worker without handles (404). */
export type HandleCheck = 'available' | 'taken' | 'invalid' | 'unsupported';
